import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import s from './returnSummary.module.css';
import { FeeSummary } from './FeeSummary';
import { ItemCard } from '../ReturnItem';
import { ReturnSummaryItems } from '../../react-components/ReturnSummaryItems';
import SummaryDetails from '../../react-components/SummaryDetails';
import { hasMultiOrder } from '../../react-components/AdditionalOrderForm/utils';
import { returnItemsService } from '../../services';
import { Language } from '../../react-components/Language';

const mapStateToProps = ({
  language,
  page: { productType } = {},
  returnsSettings: {
    [productType]: {
      refunds: { enable_api: refundApiEnabled = false } = {},
      useHandlingFeeAsRepairCost,
    } = {},
  } = {},
  returnItems: { cartActions = [] },
}) => ({
  language,
  refundApiEnabled,
  useHandlingFeeAsRepairCost,
  cartActions,
});

const ReturnSummaryComponent = ({
  language,
  useHandlingFeeAsRepairCost,
  refundApiEnabled,
  product,
  returnSubtotal,
  returnCredit,
  restockingFee,
  netCost,
  recyclingFeeReversal,
  returnTax,
  returnCost,
  discountReversal,
  expressUpgradeFee,
  handlingFee,
  creditReversalAmount,
  estimatedRefund,
  nonMonetaryAttributes,
  cartActions,
}) => {
  const hasMultipleOrders = hasMultiOrder({ returnItems: returnItemsService.getItems() });
  const returnItems = returnItemsService.getItems();
  const onlyBlindItems = returnItems.filter((item) => !item.blind_item).length === 0;
  const showNonMonetaryAttributes =
    nonMonetaryAttributes && Object.keys(nonMonetaryAttributes).length;

  return (
    <div>
      <div className={s.summary_section_container}>
        <Language
          tagName="h4"
          className={cx(s.summary_key, 'summary_key')}
          langKey={[
            cartActions?.length === 1 ? `returns.step_3.summary_header.${cartActions[0]}` : null,
            'returns.step_3.summary_header',
          ]}
          aria-level="3"
        />
        <div className="clearfix" />
      </div>
      <div className={s.summary_section_container}>
        {hasMultipleOrders ? (
          <ReturnSummaryItems />
        ) : (
          <ul className={s.return_item_list}>
            {returnItems?.map((item, index) => (
              <li key={item.item_id}>
                <return-item>
                  <ItemCard
                    item={item}
                    type="summary"
                    blindItem={item.blind_item}
                    itemQty="return_qty"
                    itemIndex={index}
                    itemSubtotal={useHandlingFeeAsRepairCost ? handlingFee : item.total_price}
                    product={product}
                  />
                </return-item>
              </li>
            ))}
          </ul>
        )}
      </div>
      {refundApiEnabled && <SummaryDetails />}
      <div className="summary-fees-container">
        <FeeSummary
          returnSubtotal={returnSubtotal}
          returnCredit={returnCredit}
          restockingFee={restockingFee}
          netCost={netCost}
          recyclingFeeReversal={recyclingFeeReversal}
          returnTax={returnTax}
          returnCost={returnCost}
          discountReversal={discountReversal}
          expressUpgradeFee={expressUpgradeFee}
          handlingFee={handlingFee}
          creditReversalAmount={creditReversalAmount}
          onlyBlindItems={onlyBlindItems}
          estimatedRefund={estimatedRefund}
        />
      </div>

      {showNonMonetaryAttributes && (
        <div className={s.non_monetary_attribute_wrapper}>
          <h4>{language['returns.step_3.non_monetary_attributes.title']}</h4>
          {Object.entries(nonMonetaryAttributes).map(([key, value]) => (
            <div
              className={cx(
                'non-monetary-attribute',
                s.non_monetary_attribute,
                `non-monetary-attribute-${key}`,
              )}
            >
              <div
                className={cx('non-monetary-label', s.non_monetary_attribute_key, {
                  ['is-empty']: value === '' || value === null,
                  // eslint-disable-next-line eqeqeq
                  ['is-zero']: value == 0,
                })}
                data-test={`non-monetary-attribute-key-${key}`}
              >
                {language[`returns.step_3.non_monetary_attributes.${key}.label`]}
              </div>
              <div
                className={cx('non-monetary-value', s.non_monetary_attribute_value, {
                  ['is-empty']: value === '' || value === null,
                  // eslint-disable-next-line eqeqeq
                  ['is-zero']: value == 0,
                })}
                data-test={`non-monetary-attribute-key-${value}`}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

ReturnSummaryComponent.defaultProps = {
  nonMonetaryAttributes: undefined,
};

ReturnSummaryComponent.propTypes = {
  language: PropTypes.object.isRequired,
  useHandlingFeeAsRepairCost: PropTypes.bool,
  refundApiEnabled: PropTypes.bool.isRequired,
  product: PropTypes.string.isRequired,
  returnSubtotal: PropTypes.number,
  returnCredit: PropTypes.number,
  restockingFee: PropTypes.number,
  netCost: PropTypes.number,
  recyclingFeeReversal: PropTypes.number,
  returnTax: PropTypes.number,
  returnCost: PropTypes.number,
  discountReversal: PropTypes.number,
  expressUpgradeFee: PropTypes.number,
  handlingFee: PropTypes.number,
  creditReversalAmount: PropTypes.number,
  estimatedRefund: PropTypes.number,
  nonMonetaryAttributes: PropTypes.object,
};

export const ReturnSummary = connect(mapStateToProps)(ReturnSummaryComponent);
